import { ReportLang } from "../i18n/reportLang";

/**
 * Locale per report language. Drives thousands separators (2,645 vs 2 645) and
 * date order (04/22/2026 vs 22.04.2026 vs 2026/04/22).
 */
const LOCALE: Record<ReportLang, string> = {
  en: "en-US",
  ru: "ru-RU",
  zh: "zh-CN",
};

/**
 * Pulls the leading number out of a payload amount.
 *
 * Several payload fields glue a unit onto the value — "413 kcal", "103 g",
 * "2645 kcal/day" — which makes the unit untranslatable as delivered. Parsing the
 * number lets the caller re-attach a localized unit from the dictionary.
 */
export const parseAmount = (raw?: string | number | null): number | null => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const match = String(raw ?? "").match(/-?[\d.,]+/);
  if (!match) return null;
  const value = Number(match[0].replace(/[^\d.-]/g, ""));
  return Number.isFinite(value) ? value : null;
};

/** Formats a payload amount for display, optionally with a localized unit. */
export const formatAmount = (
  raw: string | number | undefined | null,
  lang: ReportLang,
  unit?: string
): string => {
  const value = parseAmount(raw);
  if (value === null) return "—";
  const formatted = value.toLocaleString(LOCALE[lang]);
  return unit ? `${formatted} ${unit}` : formatted;
};

/**
 * Reformats the payload's test date for the reader's locale.
 *
 * The backend sends US month-first `MM/DD/YYYY` (optionally followed by a time),
 * which reads as a different day to a Russian or Chinese reader. Parsed explicitly
 * rather than with `new Date(string)`, whose handling of that format is
 * implementation-defined. Anything unparseable is passed through untouched, so a
 * format change upstream degrades to the raw value instead of "Invalid Date".
 */
export const formatReportDate = (
  raw: string | undefined | null,
  lang: ReportLang
): string | undefined => {
  if (!raw) return undefined;
  const text = String(raw).trim();
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(.*)$/);
  if (!match) return text;

  const [, mm, dd, yyyy, rest] = match;
  const month = Number(mm);
  const day = Number(dd);
  const year = Number(yyyy);
  if (month < 1 || month > 12 || day < 1 || day > 31) return text;

  const date = new Date(year, month - 1, day);
  // Guard against a rolled-over date (e.g. 02/31) rather than showing a wrong day.
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return text;

  const formatted = date.toLocaleDateString(LOCALE[lang], {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  // Preserve any trailing time component the payload appended.
  return `${formatted}${rest ?? ""}`;
};
